/* eslint-disable no-eval */
/* eslint-disable consistent-return */
import React, { useState, createRef } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { MentionsInput, Mention } from 'react-mentions'
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DoneIcon from '@mui/icons-material/Done'

import { Logger, LoggerTypes } from '../../../utils'
import { Button, buttonModes, buttonTypes } from '../../buttons'
import './style.scss'

const TRIGGER_MENTION = '@'

const defaultStyle = {
  control: {
    backgroundColor: '#fff',
    fontSize: 14,
    fontWeight: 'normal'
  },

  '&multiLine': {
    control: {
      fontFamily: 'monospace',
      minHeight: 63
    },
    highlighter: {
      padding: 9,
      border: '1px solid transparent'
    },
    input: {
      padding: 9,
      border: '1px solid silver'
    }
  },

  '&singleLine': {
    display: 'inline-block',
    width: 180,

    highlighter: {
      padding: 1,
      border: '2px inset transparent'
    },
    input: {
      padding: 1,
      border: '2px inset'
    }
  },

  suggestions: {
    list: {
      backgroundColor: 'white',
      border: '1px solid rgba(0,0,0,0.15)',
      fontSize: 14
    },
    item: {
      padding: '5px 15px',
      '&focused': {
        backgroundColor: '#eaeaea'
      }
    }
  }
}

const defaultMentionStyle = { backgroundColor: '#eaeaea' }

/**
 * Display a confirm popin
 *
 * @param {object} param Ex: {
 *                          openDialog: true // hook value
 *                          setOpenDialog: () => { ... }  // hook function
 *                          parameters: [{...}, {...}, ...]
 *                          onValidate: (number) => number
 *                        }
 * @returns {Dialog}
 */
export const DialogFormula = ({ openDialog, setOpenDialog, parameters, onValidate }) => {
  const { t } = useTranslation()
  let inputEl = createRef()

  const [value, setValue] = useState('')

  let hasError = false

  // -- remove the parameter name
  // -- replace the parameter ID by its value
  const calculatedValue =
    value &&
    value !== '' &&
    value.replace(/@\[(.*?)\]/g, '').replace(/{(.*?)}/gi, (match, id) => {
      let val = ''
      // -- foreach 'id' try to find the corresponding parameter.id
      parameters.forEach((el) => {
        if (el.id === id) {
          val = el.value
        }
      })
      return val
    })

  const equation = calculatedValue && calculatedValue !== '' && calculatedValue

  let result = ''
  try {
    result = calculatedValue && calculatedValue !== '' && eval(calculatedValue)
  } catch (e) {
    result = 'Error in equation'
    hasError = true
  }

  const onCloseDialog = () => setOpenDialog(false)

  const onClickNo = (e) => {
    e.stopPropagation()

    Logger.trace(LoggerTypes.CONTROLLER, 'DialogFormula.onClickNo()', { openDialog })

    onCloseDialog()
  }

  const onClickYes = (e) => {
    e.stopPropagation()

    const val = inputEl.current
    const _formula =
      value && value !== '' && value.replace(/@\[(.*?)\]/g, '').replace(/{(.*?)}/gi, (match, id) => `@${id}`)

    Logger.trace(LoggerTypes.CONTROLLER, 'DialogFormula.onClickYes()', { val, _formula })

    onCloseDialog()

    if (typeof onValidate === 'function') {
      onValidate(result, equation, _formula)
    }
  }

  const onValueChange = (e, newValue, newFormula, mentions) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'DialogFormula.onValueChange()', { newValue, newFormula, mentions })

    setValue(newValue)
  }

  const data = parameters.map((el) => ({ id: el.id, display: el.name }))

  Logger.trace(LoggerTypes.CONTAINER, 'DialogFormula.render()', { parameters, data, value, calculatedValue })

  return (
    <Dialog
      className="DialogFormula"
      open={openDialog}
      onClose={onCloseDialog} // <-- close while clicking on background
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('common.dialogs.formula.title')}</DialogTitle>
      <DialogContent>
        <MentionsInput
          value={value}
          onChange={onValueChange}
          style={defaultStyle}
          inputRef={inputEl}
          placeholder={t('common.dialogs.formula.placeholder')}
          a11ySuggestionsListLabel={t('common.dialogs.formula.list')}
          ignoreAccents={true}
          singleLine={false}
          allowSpaceInQuery={true}
          allowSuggestionsAboveCursor={false}
        >
          <Mention
            trigger={TRIGGER_MENTION}
            data={data}
            markup="@[__display__]{__id__}"
            renderSuggestion={(suggestion, search, highlightedDisplay, index, focused) => (
              <div className={`parameter ${focused ? 'focused' : ''}`}>{highlightedDisplay}</div>
            )}
            displayTransform={(id, display) => `@${display}`}
            style={defaultMentionStyle}
            appendSpaceOnAdd={true}
          />
        </MentionsInput>
        <div className="calculatedFormula">
          <b>Equation: </b>
          <span style={{ paddingLeft: '10px' }}>{equation}</span>
        </div>
        <div className="calculatedResult">
          <b>Result: </b>
          <span style={{ paddingLeft: '27px' }}>{result}</span>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          mode={buttonModes.SECONDARY_ACTION}
          type={buttonTypes.BUTTON}
          text={t('common.cancel')}
          icon={<CloseIcon />}
          onClick={onClickNo}
        />
        <Button
          className={hasError ? 'buttonError' : ''}
          mode={buttonModes.PRIMARY_ACTION}
          type={buttonTypes.BUTTON}
          text={hasError ? t('common.error') : t('common.validate')}
          icon={hasError ? <CloseIcon /> : <DoneIcon />}
          onClick={(e) => {
            if (!hasError) {
              onClickYes(e)
            }
          }}
        />
      </DialogActions>
    </Dialog>
  )
}

DialogFormula.displayName = 'DialogFormula'

DialogFormula.propTypes = {
  openDialog: PropTypes.bool.isRequired,
  setOpenDialog: PropTypes.func.isRequired,
  parameters: PropTypes.arrayOf(PropTypes.object).isRequired,
  onValidate: PropTypes.func
}

DialogFormula.defaultProps = {
  openDialog: false,
  setOpenDialog: () => {},
  parameters: [],
  onValidate: null
}
